import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { PiFileLight, PiFileDocLight, PiFilePdfLight, PiDownloadLight, PiArrowsOutLight } from 'react-icons/pi';
import { fetchFolderContents, streamFile } from '../../../utils/FileApiBuilder';
import { showSidePanel, hideSidePanel } from '../../../store/actions/sidePanelActions';
import Table from '../../shared/TailwindComponents/Table';
import Button from '../../shared/TailwindComponents/Button';
import Badge from '../../shared/TailwindComponents/Badge';
import dayjs from 'dayjs';
import { formatDateTime } from '../../../utils/functions';

interface MatterDocumentsProps {
  matterUuid: string;
}

const getFileIcon = (fileName: string, mimeType?: string) => {
  const ext = fileName?.split('.').pop()?.toLowerCase() || '';
  if (ext === 'pdf' || mimeType?.includes('pdf')) {
    return <PiFilePdfLight className="w-4 h-4 text-red-500" />;
  }
  if (['docx', 'doc'].includes(ext) || mimeType?.includes('word')) {
    return <PiFileDocLight className="w-4 h-4 text-blue-500" />;
  }
  return <PiFileLight className="w-4 h-4 text-gray-500" />;
};

const getFileExtBadge = (fileName: string) => {
  const ext = fileName?.split('.').pop() || '';
  const colorMap: Record<string, string> = {
    PDF: 'red',
    DOCX: 'blue',
    DOC: 'blue',
    XLSX: 'green',
    XLS: 'green',
    TXT: 'gray',
  };
  return <Badge color={(colorMap[ext] || 'gray') as any} className="text-xs">{ext}</Badge>;
};

const MatterDocuments: React.FC<MatterDocumentsProps> = ({ matterUuid }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [files, setFiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDocuments = useCallback(async () => {
    if (!matterUuid) return;
    setLoading(true);
    setError(null);
    try {
      // The matter's folder UUID = matterUuid (set during creation)
      // Parent folder is the Matters root folder, but we can pass undefined
      const result = await fetchFolderContents(matterUuid, 1, undefined, {
        limit: 50,
        sortBy: 'created_at',
        sortOrder: 'desc',
      });
      setFiles(result?.files || []);
    } catch (err: any) {
      // Folder might not exist yet — not an error for the user
      if (err?.response?.status === 404) {
        setFiles([]);
      } else {
        console.error('Failed to load matter documents:', err);
        setError(t('Failed to load documents'));
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matterUuid]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleOpenFile = async (file: any) => {
    const fileId = file.FileID || file.uuid || file.id;
    const fileName = file.FileName || file.name || 'Document';
    const mimeType = (file.MimeType || file.mime_type || '').toLowerCase();
    const isPdf = mimeType.includes('pdf') || fileName.toLowerCase().endsWith('.pdf');

    try {
      // Stream the file content
      const blob = await streamFile(fileId, matterUuid);
      let content = '';
      let fileUrl = '';

      if (isPdf) {
        fileUrl = URL.createObjectURL(blob);
      } else {
        // For DOCX/HTML content, read as text
        content = await blob.text();
        if (!content.includes('<') || !content.includes('>')) {
          // If it's not HTML, create a blob URL for download
          fileUrl = URL.createObjectURL(blob);
        }
      }

      dispatch(
        showSidePanel({
          type: 'fileReaderTst',
          showSidePanel: true,
          width: '70%',
          fileUrl: fileUrl || undefined,
          content: content || undefined,
          isPdf,
          filename: fileName,
          loading: false,
          message: fileName,
          subMsg: t('Document Preview'),
        })
      );
    } catch (err) {
      console.error('Failed to open file:', err);
    }
  };

  const handleDownload = async (file: any) => {
    const fileId = file.FileID || file.uuid || file.id;
    const fileName = file.FileName || file.name || 'Document';

    try {
      const blob = await streamFile(fileId, matterUuid, undefined, 'attachment');
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download file:', err);
    }
  };

  if (loading) {
    return (
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 bg-white dark:bg-gray-800 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{t('Documents')}</h3>
        <div className="animate-fadePulse space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 bg-gray-200 dark:bg-gray-700 rounded" />
          ))}
        </div>
      </div>
    );
  }

  const tableData = files.map((file) => {
    const fileName = file.FileName || file.name || 'Untitled';
    const uploadDate = file.created_at || file.CreatedAt;
    const uploadedBy = file.uploaded_by_user_name || file.UploadedBy || '';
    const mimeType = file.MimeType || file.mime_type || '';

    return {
      id: file.FileID || file.uuid || file.id,
      Name: fileName,
      Type: fileName,
      'Uploaded By': uploadedBy,
      Date: formatDateTime(uploadDate),
      _raw: file,
    };
  });

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 bg-white dark:bg-gray-800 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('Finalized Documents')}</h3>
        <span className="text-xs text-gray-500">{files.length} {files.length === 1 ? t('file') : t('files')}</span>
      </div>

      {files.length > 0 ? (
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded">
          <Table
            columns={['Name', 'Date']}
            data={tableData}
            renderCell={(column, value, row) => {
              if (column === 'Name') {
                return (
                  <div className="flex items-center gap-2">
                    {/* {getFileIcon(value, row._raw?.MimeType || row._raw?.mime_type)} */}
                    <span className="text-sm text-gray-900 dark:text-gray-200 truncate max-w-[200px]" title={value}>
                      {value}
                    </span>
                  </div>
                );
              }
            //   if (column === 'Type') {
            //     return getFileExtBadge(value);
            //   }
            //   return undefined;
            }}
            onRowClick={(row) => handleOpenFile(row._raw)}
            actions={[
              {
                label: t('View'),
                icon: <PiArrowsOutLight className="h-4 w-4" />,
                onClick: (row) => handleOpenFile(row._raw),
              },
              {
                label: t('Download'),
                icon: <PiDownloadLight className="h-4 w-4" />,
                onClick: (row) => handleDownload(row._raw),
              },
            ]}
            max="max-h-[300px]"
            min="min-h-[100px]"
          />
        </div>
      ) : (
        <div className="text-center py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
          <PiFileLight className="mx-auto h-10 w-10 text-gray-400 mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('No finalized documents yet.')}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            {t('Generate and finalize drafts or insights from the Drafts or Strategy tabs.')}
          </p>
        </div>
      )}
    </div>
  );
};

export default MatterDocuments;
